import React, { useState } from 'react';
import { Location, Measurement, MeteoSchweizData } from '../types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, ReferenceLine } from 'recharts';
import { getSwissTimeComponents } from '../utils/timezoneUtils';

interface LocationComparisonProps {
  locations: Location[];
  measurements: Measurement[];
  meteoSchweizData?: MeteoSchweizData[];
  showMeteoSchweiz?: boolean;
  selectedLocation?: Location | null;
}

interface ComparisonDataItem {
  id: number;
  name: string;
  avgTemp: number;
  maxTemp: number;
  minTemp: number;
  measurements: number;
  perceivedCount: number;
  mostCommonPerceived: string;
  color: string;
}

const LocationComparison: React.FC<LocationComparisonProps> = ({ 
  locations, 
  measurements, 
  meteoSchweizData = [],
  showMeteoSchweiz = false,
  selectedLocation 
}) => {
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');

  // Initialize with selected location if available
  React.useEffect(() => {
    if (selectedLocation) {
      setSelectedLocations([selectedLocation.id]);
    }
  }, [selectedLocation]);

  const getTemperatureColor = (temp: number) => {
    if (temp < 6) return '#284496';      // Sehr kalt
    if (temp < 11) return '#5d6ab0';     // Kalt
    if (temp < 15) return '#9498cc';     // Kühl
    if (temp < 19) return '#cacbe7';     // Leicht kühl
    if (temp < 22) return '#ffffff';     // Behaglich
    if (temp < 26) return '#f0cdbe';     // Leicht warm
    if (temp < 32) return '#e09a80';     // Warm
    if (temp < 37) return '#cf674a';     // Heiss
    return '#be331e';                    // Sehr heiss
  };

  const getPerceivedColor = (perceived: string) => {
    switch (perceived) {
      case 'Sehr kalt': return '#284496';
      case 'Kalt': return '#5d6ab0';
      case 'Kühl': return '#9498cc';
      case 'Leicht kühl': return '#cacbe7';
      case 'Behaglich': return '#ffffff';
      case 'Leicht warm': return '#f0cdbe';
      case 'Warm': return '#e09a80';
      case 'Heiss': return '#cf674a';
      case 'Sehr heiss': return '#be331e';
      default: return '#f3f4f6';
    }
  };

  // Define distinct colors for different locations
  const getLocationColor = (locationId: number, index: number) => {
    const colors = [
      '#6bbc89', // Primary green
      '#EE8662', // Accent orange
      '#284496', // Dark blue
      '#cf674a', // Red
      '#9498cc', // Purple
      '#f0cdbe', // Light orange
      '#5d6ab0', // Blue
      '#e09a80', // Peach
    ];
    return colors[index % colors.length];
  };

  const handleLocationToggle = (locationId: number) => {
    setSelectedLocations(prev => 
      prev.includes(locationId) 
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  // Prepare data for comparison
  const comparisonData: ComparisonDataItem[] = selectedLocations
    .map(locationId => {
      const location = locations.find(l => l.id === locationId);
      const locationMeasurements = measurements.filter(m => m.location_id === locationId);
      
      if (!location || locationMeasurements.length === 0) return null;

      const avgTemp = locationMeasurements.reduce((sum, m) => sum + m.measured_temperature, 0) / locationMeasurements.length;
      const maxTemp = Math.max(...locationMeasurements.map(m => m.measured_temperature));
      const minTemp = Math.min(...locationMeasurements.map(m => m.measured_temperature));

      // Calculate perceived temperature statistics
      const perceivedMeasurements = locationMeasurements.filter(m => m.perceived_temperature && m.perceived_temperature.trim() !== '');
      const perceivedCounts: { [key: string]: number } = {};
      perceivedMeasurements.forEach(m => {
        perceivedCounts[m.perceived_temperature] = (perceivedCounts[m.perceived_temperature] || 0) + 1;
      });
      const mostCommonPerceived = Object.keys(perceivedCounts).length > 0
        ? Object.entries(perceivedCounts).reduce((a, b) => a[1] > b[1] ? a : b)[0]
        : 'Keine Daten';

      return {
        id: locationId,
        name: location.name,
        avgTemp,
        maxTemp,
        minTemp,
        measurements: locationMeasurements.length,
        perceivedCount: perceivedMeasurements.length,
        mostCommonPerceived,
        color: getTemperatureColor(avgTemp)
      };
    })
    .filter((item): item is ComparisonDataItem => item !== null);

  // Time series data for line chart
  const timeSeriesData = selectedLocations.length > 0 ? (() => {
    // Get all measurements for selected locations
    const relevantMeasurements = measurements.filter(m => selectedLocations.includes(m.location_id));
    
    console.log('Debug Zeitverlauf:', {
      selectedLocations,
      totalMeasurements: measurements.length,
      relevantMeasurements: relevantMeasurements.length,
      sampleMeasurements: relevantMeasurements.slice(0, 3).map(m => ({
        location_id: m.location_id,
        temperature: m.measured_temperature,
        created_at: m.created_at
      }))
    });
    
    if (relevantMeasurements.length === 0) {
      console.log('No relevant measurements found');
      return [];
    }
    
    // Create time slots for all hours (6-22)
    const timeSlots: { [key: string]: { [locationId: number]: number[] } } = {};
    
    // Initialize time slots
    for (let hour = 6; hour <= 22; hour++) {
      const timeKey = `${hour.toString().padStart(2, '0')}:00`;
      timeSlots[timeKey] = {};
      selectedLocations.forEach(id => timeSlots[timeKey][id] = []);
    }
    
    // Group measurements by hour and location (using Swiss time)
    relevantMeasurements.forEach(measurement => {
      const swissTimeComponents = getSwissTimeComponents(measurement.created_at);
      const hour = swissTimeComponents.hours;
      const timeKey = `${hour.toString().padStart(2, '0')}:00`;
      
      if (timeSlots[timeKey] && timeSlots[timeKey][measurement.location_id]) {
        timeSlots[timeKey][measurement.location_id].push(measurement.measured_temperature);
      }
    });
    
    // Add MeteoSchweiz data to time slots
    if (showMeteoSchweiz && meteoSchweizData.length > 0) {
      meteoSchweizData.forEach(data => {
        const date = new Date(data.reference_timestamp);
        const hour = date.getHours();
        const timeKey = `${hour.toString().padStart(2, '0')}:00`;
        
        if (timeSlots[timeKey] && data.tre200h0 > 0) {
          // Add MeteoSchweiz data to each time slot
          Object.keys(timeSlots[timeKey]).forEach(locationIdStr => {
            const locationId = parseInt(locationIdStr);
            if (!timeSlots[timeKey][locationId]) {
              timeSlots[timeKey][locationId] = [];
            }
          });
        }
      });
    }
    
    // Convert to chart data format with averages
    const dataPoints = Object.entries(timeSlots).map(([time, locationData]) => {
      const dataPoint: any = { time };
      selectedLocations.forEach(locationId => {
        const temps = locationData[locationId] || [];
        dataPoint[`location_${locationId}`] = temps.length > 0 
          ? temps.reduce((a, b) => a + b, 0) / temps.length 
          : null;
      });
      
      // Add MeteoSchweiz data for this time slot
      if (showMeteoSchweiz && meteoSchweizData.length > 0) {
        const [hour] = time.split(':').map(Number);
        const meteoData = meteoSchweizData.find(data => {
          const dataHour = new Date(data.reference_timestamp).getHours();
          return dataHour === hour && data.tre200h0 > 0;
        });
        dataPoint.meteoSchweiz = meteoData ? meteoData.tre200h0 : null;
      }
      
      return dataPoint;
    });

    console.log('Time series data:', {
      selectedLocations,
      relevantMeasurementsCount: relevantMeasurements.length,
      dataPoints: dataPoints.slice(0, 5), // Show first 5 for debugging
      dataPointsLength: dataPoints.length,
      firstDataPoint: dataPoints[0],
      chartType: chartType
    });

    return dataPoints;
  })() : [];

  return (
    <div className="bg-white rounded-lg p-6 shadow-md">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 md:mb-0">Standortvergleich</h3>
        <div className="flex space-x-2 md:justify-end">
          <button
            onClick={() => setChartType('line')}
            className={`flex-1 md:flex-none px-3 py-1 rounded text-sm ${
              chartType === 'line' 
                ? 'bg-primary text-white' 
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            Zeitverlauf
          </button>
          <button
            onClick={() => setChartType('bar')}
            className={`flex-1 md:flex-none px-3 py-1 rounded text-sm ${
              chartType === 'bar' 
                ? 'bg-primary text-white' 
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            Durchschnitt
          </button>
        </div>
      </div>

      {/* Location Selection */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Standorte auswählen:</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {locations.map(location => (
            <button
              key={location.id}
              onClick={() => handleLocationToggle(location.id)}
              className={`p-2 rounded text-sm text-left transition-colors ${
                selectedLocations.includes(location.id)
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="font-medium">{location.name}</div>
            </button>
          ))}
        </div>
      </div>

      {selectedLocations.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Wählen Sie mindestens einen Standort aus, um einen Vergleich zu starten.
        </div>
      ) : (
        <div className="space-y-6">
          {/* Chart */}
          <div className="h-64">
            {chartType === 'line' ? (
              (() => {
                console.log('Chart rendering debug:', {
                  chartType,
                  timeSeriesDataLength: timeSeriesData.length,
                  selectedLocations,
                  firstDataPoint: timeSeriesData[0]
                });
                return timeSeriesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={timeSeriesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis label={{ value: 'Temperatur (°C)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Legend />
                      {selectedLocations.map((locationId, index) => {
                        const location = locations.find(l => l.id === locationId);
                        return (
                          <Line
                            key={locationId}
                            type="monotone"
                            dataKey={`location_${locationId}`}
                            name={location?.name || `Standort ${locationId}`}
                            stroke={getLocationColor(locationId, index)}
                            strokeWidth={2}
                            dot={{ fill: getLocationColor(locationId, index), strokeWidth: 2, r: 4 }}
                          />
                        );
                      })}
                      
                      {/* MeteoSchweiz reference line */}
                      {showMeteoSchweiz && (
                        <Line
                          type="monotone"
                          dataKey="meteoSchweiz"
                          name="MeteoSchweiz Daten"
                          stroke="#6b7280"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          strokeOpacity={0.7}
                          dot={false}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-500">
                    Keine Zeitreihendaten verfügbar
                  </div>
                );
              })()
            ) : (
              comparisonData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={comparisonData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis label={{ value: 'Temperatur (°C)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      align="left"
                      className="md:!text-center"
                    />
                    <Bar dataKey="avgTemp" name="Ø Temperatur" fill="#8884d8" />
                    <Bar dataKey="maxTemp" name="Max. Temperatur" fill="#82ca9d" />
                    
                    {/* Add MeteoSchweiz reference line if available */}
                    {showMeteoSchweiz && meteoSchweizData.length > 0 && (
                      <ReferenceLine
                        y={meteoSchweizData.reduce((sum, data) => sum + data.tre200h0, 0) / meteoSchweizData.length}
                        stroke="#6b7280"
                        strokeOpacity={0.7}
                        strokeDasharray="3 3"
                        label="MeteoSchweiz Ø"
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  Keine Vergleichsdaten verfügbar
                </div>
              )
            )}
          </div>

          {/* Perceived Temperature Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Gefühlte Temperaturen im Vergleich</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {comparisonData.map((location) => (
                <div key={location.id} className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-800">{location.name}</span>
                    <div 
                      className="w-4 h-4 rounded-full border border-gray-300" 
                      style={{ backgroundColor: getPerceivedColor(location.mostCommonPerceived) }}
                    ></div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <div>Häufigste Empfindung: <span className="font-medium">{location.mostCommonPerceived}</span></div>
                    <div>Anzahl Bewertungen: {location.perceivedCount}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison Table */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Datenübersicht</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Standort
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ø Temp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Max Temp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Min Temp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Häufigste Empfindung
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Messungen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {comparisonData.map((location) => (
                    <tr key={location.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {location.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {location.avgTemp.toFixed(1)}°C
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {location.maxTemp.toFixed(1)}°C
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {location.minTemp.toFixed(1)}°C
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {location.mostCommonPerceived}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {location.measurements} ({location.perceivedCount} gefühlt)
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationComparison;
